/**
 * Interaction layout calculator.
 *
 * Calculates arrow paths and message label positions for interactions
 * between participants.
 */

import type { Interaction } from '../../../parser/types';
import type {
  LayoutConfig,
  ParticipantLayout,
  InteractionLayout,
} from './types';

/**
 * Calculates layout positions for interactions (arrows between participants).
 */
export class InteractionLayoutCalculator {
  private interactions: Interaction[];
  private participants: Map<string, ParticipantLayout>;
  private config: LayoutConfig;
  private startY: number;

  constructor(
    interactions: Interaction[],
    participants: Map<string, ParticipantLayout>,
    config: LayoutConfig,
    startY?: number
  ) {
    this.interactions = interactions;
    this.participants = participants;
    this.config = config;

    // Use provided startY or calculate default
    if (startY !== undefined) {
      this.startY = startY;
    } else {
      const firstParticipant = Array.from(this.participants.values())[0];
      this.startY = firstParticipant
        ? firstParticipant.lifelineStart + this.config.interactionSpacing
        : this.config.marginTop +
          this.config.participantHeight +
          this.config.interactionSpacing;
    }
  }

  /**
   * Calculates layouts for all interactions.
   *
   * Interactions are stacked vertically with equal spacing, starting
   * from the provided or calculated starting Y position.
   *
   * @returns Array of interaction layouts in order
   */
  public calculateLayouts(): InteractionLayout[] {
    const layouts: InteractionLayout[] = [];
    let currentY = this.startY;

    for (const interaction of this.interactions) {
      const fromParticipant = this.participants.get(interaction.from);
      const toParticipant = this.participants.get(interaction.to);

      if (!fromParticipant || !toParticipant) {
        // Skip interactions with unknown participants
        continue;
      }

      const startX = fromParticipant.centerX;
      const endX = toParticipant.centerX;
      const labelX = (startX + endX) / 2;

      layouts.push({
        from: interaction.from,
        to: interaction.to,
        startX,
        startY: currentY,
        endX,
        endY: currentY, // Horizontal arrows have same Y
        midY: currentY,
        labelX,
        arrowType: interaction.arrow,
      });

      // Move down for next interaction
      currentY += this.config.interactionSpacing;
    }

    return layouts;
  }
}
